import React, { BaseSyntheticEvent, useEffect, useRef, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonPopover,
  IonRippleEffect,
  IonSearchbar,
  IonSpinner,
  IonToolbar,
} from '@ionic/react'
import { ISingleSelectCustomInput, IOption, IPNNVOption, IOptions } from '../../common/common.types'
import { caretDownOutline } from 'ionicons/icons'
import { isTablet } from '../../utilities'
import './SingleSelect.component.css'
import { useTranslation } from 'react-i18next'
import { ResizableBox } from 'react-resizable'
import { useDispatch, useSelector } from 'react-redux'
import { initializeSearch, setSearchText } from '../search/store/search.actions'
import { getSearchStateSelector } from '../search/selectors'
import { getStyleForPHI, IPHIComponent } from '../phi/phi.component'
import { isIOSDevice } from '../../utilities/isiOS.util'
import SInput from '../stringInput/StringInput.component'
import Validation from '../validation/Validation.component'
import ValidationBadgeComponent from '../validationBadge/ValidationBadge.component'
import { getPNNVStateSelector } from '../pnnv/selectors'
import { resetPNNVState, setNumberPadAndCalendarPNNVIsOpen } from '../pnnv/store/pnnv.actions'
import { IAlertState } from '../alert/store/alert.types'
import { showAlert } from '../alert/store/alert.actions'
import { getCalculatedHeight } from '../../utilities/popover.util'

export interface ISingleSelectProps extends IPHIComponent {
  dataTestID: string
  listHeader?: string
  pnnvListHeader?: string
  label?: string
  options: any
  selectedOption?: any
  selectedPNNVOption?: IPNNVOption | null
  dispatchData?: any
  action: (option: any, dispatchData?: any) => void
  pnnvAction?: (option: any, dispatchData?: any) => void
  disabled?: boolean
  customInput?: ISingleSelectCustomInput
  hideClearButton?: boolean
  showSelectedOptionId?: string
  reduxAction?: string
  showPNNVs?: boolean
  nemsis?: string
  shouldRenderPNNVBar?: boolean
  showPNNVAsNormals?: boolean
  pnnvDoneAction?: () => void
  showSingleSelectPopup?: boolean | undefined
  pnnvRenderedFromFreeTextField?: boolean
  hidePopup?: () => void
  actionOnCannotComplete?: () => void
  overrideStateValue?: boolean
}

const SingleSelect: React.FC<ISingleSelectProps> = (props: ISingleSelectProps) => {
  const { t } = useTranslation()
  const {
    dataTestID,
    listHeader,
    pnnvListHeader = '',
    label,
    options,
    action,
    pnnvAction = null,
    dispatchData,
    selectedOption,
    selectedPNNVOption = null,
    isPHI,
    disabled,
    customInput,
    hideClearButton,
    showSelectedOptionId = '',
    reduxAction,
    showPNNVs = true,
    nemsis = 'nemsisCode',
    shouldRenderPNNVBar = false,
    showPNNVAsNormals = false,
    pnnvDoneAction,
    showSingleSelectPopup,
    pnnvRenderedFromFreeTextField,
    hidePopup,
    actionOnCannotComplete,
    overrideStateValue = false,
  } = props

  const [showModal, setShowModal] = useState(false)
  const [popoverState, setShowPopover] = useState({ showPopover: false, event: undefined })
  const [selectedLabel, setSelectedLabel] = useState('')
  const isTabletDevice = isTablet()
  const dispatch = useDispatch()
  const search = useSelector(getSearchStateSelector)
  const pnnvState = useSelector(getPNNVStateSelector)

  const { isLoading, filteredOptions, searchText } = search
  const isIOS = isIOSDevice()
  const [pnnvOptions, setPnnvOptions] = useState<IOptions | null>(null)
  const hiddenPNNVBarRef = useRef<HTMLIonButtonElement>(null)
  const pnnvBarRef = useRef<HTMLIonButtonElement>(null)
  const itemRef = useRef<HTMLIonItemElement>(null)
  const [heightMeasures, setHeightMeasures] = useState(getCalculatedHeight(itemRef.current, 400))

  const handleSearch = (e: CustomEvent) => {
    dispatch(setSearchText(e.detail.value))
  }

  const preventPageScroll = (on: boolean) => {
    const content: HTMLIonContentElement | null = document.querySelector(':not(.ion-page-hidden) > ion-content')
    if (on) {
      content?.classList.add('no-scroll')
    } else {
      content?.classList.remove('no-scroll')
    }
  }

  const resetReduxState = () => {
    if (!search.options.length) {
      dispatch(initializeSearch([]))
    }
  }

  useEffect(() => {
    if (selectedOption && selectedOption.name) {
      setSelectedLabel(selectedOption.name)
    } else {
      setSelectedLabel('')
    }
  }, [selectedOption])

  useEffect(() => {
    const freeTextFieldIsOpen = pnnvState.freeTextField.isOpen
    if (freeTextFieldIsOpen === true) {
      if (hiddenPNNVBarRef.current) hiddenPNNVBarRef.current.click()
    }
    if (freeTextFieldIsOpen === false) {
      dismiss()
    }
  }, [pnnvState.freeTextField.isOpen])

  useEffect(() => {
    if (showSingleSelectPopup === true) {
      if (pnnvBarRef.current) pnnvBarRef.current.click()
    }
    if (showSingleSelectPopup === false) {
      dismiss()
    }
  }, [showSingleSelectPopup])

  const showSingleSelect = (e: any, calledFromFreeTextField?: boolean) => {
    preventPageScroll(true)
    const initialOptions = !shouldRenderPNNVBar ? options.filter((data: IOption) => !data.isNV && !data.isPN) : options
    dispatch(initializeSearch(initialOptions))
    if (isTabletDevice) {
      setHeightMeasures(getCalculatedHeight(itemRef.current, 400))
      if (!calledFromFreeTextField) e.persist()
      setShowPopover({ showPopover: true, event: e })
    } else {
      setShowModal(true)
    }
  }

  const dismiss = () => {
    resetReduxState()

    if (isTabletDevice) {
      resetPopoverState()
    } else {
      resetModalState()
    }
    if (pnnvAction) {
      dispatch(resetPNNVState())
    }
  }

  const clearInput = () => {
    const option = null
    if (!overrideStateValue) setSelectedLabel('')
    action(option, dispatchData)
    dismiss()
    if (pnnvAction) pnnvAction(null, dispatchData)
    pnnvDoneAction && pnnvDoneAction()
  }

  const selectOption = (e: BaseSyntheticEvent, option: IOption) => {
    if (shouldRenderPNNVBar) {
      if (pnnvRenderedFromFreeTextField && pnnvState.freeTextField.showConfirmation) {
        showConfirmationBeforeClearingRegularValues(e, option)
      } else {
        selectPNNVOption(e, option)
      }
      return
    }
    e.preventDefault()
    if (option.id !== selectedOption?.id) {
      let element: HTMLElement | null = document.getElementById(`optionLabel_${selectedOption?.id}`)
      if (element) {
        element.classList.remove('selectedLabel')
      }
      element = null
    }
    if (option.name && !overrideStateValue) {
      setSelectedLabel(option.name)
    }
    if (option.id !== customInput?.textboxEnabledId) {
      setTimeout(() => {
        dismiss()
      }, 250)
    } else {
      setTimeout(() => {
        const content: HTMLIonContentElement | null = document.querySelector('#selectList')
        content && content?.scrollBy(0, 70)
      }, 10)
    }

    action(option, dispatchData)
    if (selectedPNNVOption && pnnvAction) {
      pnnvAction(null, dispatchData)
    }
  }

  const showConfirmationBeforeClearingRegularValues = (e: BaseSyntheticEvent, option: IOption) => {
    const alertState: IAlertState = {
      header: pnnvState.freeTextField.pnnvLabel ?? '',
      message: t('pnnv.userConfirmationMessage'),
      buttons: [
        { text: t('pnnv.cancel'), handler: dismiss },
        { text: t('pnnv.continueAndClear'), role: 'destructive', handler: () => selectPNNVOption(e, option) },
      ],
      showEqualWidthButton: true,
    }
    dispatch(showAlert(alertState))
  }

  const selectPNNVOption = (e: BaseSyntheticEvent, option: IOption) => {
    if (!pnnvAction && !shouldRenderPNNVBar) return
    e.preventDefault()
    if (option.name) {
      const obj = {
        id: option.id,
        name: option.name,
        pn: '',
        nv: '',
      }
      const key = nemsis as keyof IOption
      if (option[key]) {
        if (option.isPN) {
          obj.pn = String(option[key])
        } else if (option.isNV) {
          obj.nv = String(option[key])
        }
      }
      setSelectedLabel('')
      if (shouldRenderPNNVBar) {
        action(obj as IPNNVOption, dispatchData)
        pnnvState.freeTextField.nonPNNVDataResetFunction()
        pnnvDoneAction && pnnvDoneAction()
      } else if (pnnvAction) {
        if (selectedOption) action(null, dispatchData)
        pnnvAction(obj as IPNNVOption, dispatchData)
      }
      setTimeout(() => {
        dismiss()
      }, 250)
    }
  }

  useEffect(() => {
    if (
      customInput?.textboxValue &&
      customInput?.maxlength &&
      customInput?.textboxValue.length >= customInput?.maxlength &&
      (showModal || popoverState.showPopover)
    ) {
      setTimeout(() => {
        customInput?.action && dispatch(customInput?.action(customInput?.textboxValue))
        const content: HTMLIonContentElement | null = document.querySelector('#selectList')
        content && content?.scrollBy(0, 70)
      }, 10)
    }
  }, [customInput?.textboxValue, showModal, popoverState.showPopover])

  const resetModalState = () => {
    hidePopup && hidePopup()
    setShowModal(false)
    preventPageScroll(false)
  }

  const resetPopoverState = () => {
    hidePopup && hidePopup()
    setShowPopover({ showPopover: false, event: undefined })
    preventPageScroll(false)
  }

  const getLabelValue = () => {
    if (showSelectedOptionId && selectedOption && selectedOption[showSelectedOptionId] != null)
      return `(${selectedOption[showSelectedOptionId]}) ${selectedOption?.name}`
    else return selectedLabel || selectedOption?.name
  }

  useEffect(() => {
    if (showPNNVs && !shouldRenderPNNVBar) {
      const obj = options?.filter((data: IOption) => data?.isNV || data?.isPN)
      setPnnvOptions(obj)
    }
  }, [showModal, popoverState.showPopover])

  const getPnnvSection = () => {
    return (
      showPNNVs &&
      !shouldRenderPNNVBar &&
      pnnvOptions &&
      pnnvOptions.length > 0 && (
        <div className={`${showPNNVAsNormals ? 'pnnv-selection-as-normals' : 'pnnv-selection-section'}`}>
          <hr className='picklist-divider' />
          <IonList lines='full'>
            {pnnvOptions?.map((option: IOption) => (
              <IonItem
                button
                detail={false}
                key={option.id}
                data-testid={`Option_${option.id}`}
                onClick={(e) => selectPNNVOption(e, option)}
                className={`${selectedPNNVOption?.id === option.id ? 'selected-pnnv-option' : ''} ${
                  !showPNNVAsNormals ? 'pnnv-option' : ''
                } pnnv-option-as-normals`}>
                <IonLabel id={`optionLabel_${option.id}`}>{option.name}</IonLabel>
              </IonItem>
            ))}
          </IonList>
        </div>
      )
    )
  }

  const popoverSelectBody = (
    <ResizableBox
      width={350}
      height={heightMeasures.height}
      minConstraints={[300, 300]}
      maxConstraints={[550, heightMeasures.maxHeight]}>
      <>
        <IonHeader className='ion-no-border'>
          <IonToolbar color='light'>
            <IonListHeader color='light'>
              <IonLabel>{listHeader ?? pnnvListHeader}</IonLabel>
            </IonListHeader>
          </IonToolbar>
          {!showPNNVAsNormals && (
            <IonToolbar color='light'>
              <IonSearchbar
                data-testid='Searchbar'
                debounce={0}
                value={searchText}
                onIonChange={handleSearch}
                autocomplete='off'
              />
            </IonToolbar>
          )}
        </IonHeader>

        {!showPNNVAsNormals && (
          <IonList id='selectList'>
            {isLoading && <IonSpinner data-testid='Spinner' />}
            {options && filteredOptions && (
              <>
                {filteredOptions.map((option: any) => {
                  const isTextboxEnabled =
                    selectedOption &&
                    selectedOption.id === customInput?.textboxEnabledId &&
                    option.id === customInput?.textboxEnabledId
                  return (
                    <React.Fragment key={`option_${option.id}`}>
                      <IonItem
                        data-private={isPHI}
                        style={getStyleForPHI(isPHI)}
                        button
                        detail={false}
                        data-testid={`Option_${option.id}`}
                        onClick={(e) => selectOption(e, option)}
                        className='ion-no-padding ion-activatable'>
                        <IonLabel
                          id={`optionLabel_${option.id}`}
                          className={`${
                            selectedOption && String(selectedOption.id) === String(option.id) ? 'selectedLabel' : ''
                          }
                    ${'optionLabel'}`}>
                          {showSelectedOptionId
                            ? option[showSelectedOptionId] != null
                              ? `(${option[showSelectedOptionId]}) ${option.name}`
                              : option.name
                            : option.name}
                        </IonLabel>
                      </IonItem>
                      {isTextboxEnabled && customInput?.textboxSetAction && (
                        <div data-private={isPHI} style={getStyleForPHI(isPHI)}>
                          <SInput
                            label='Describe'
                            color='primary'
                            dataTestID={`Other-Input_${customInput?.textboxEnabledId}`}
                            inputValue={customInput?.textboxValue ? customInput?.textboxValue : ''}
                            onChangeAction={customInput?.textboxSetAction}
                            maxlength={customInput?.maxlength ? customInput?.maxlength : undefined}
                          />
                          {customInput?.setTextBoxActionName && (
                            <>
                              <Validation id={customInput?.setTextBoxActionName} />
                              <hr />
                            </>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  )
                })}
              </>
            )}
            {searchText.length > 0 && !filteredOptions.length && (
              <IonItem key={'no_options_found'}>
                <IonLabel>
                  {t('common.NoSearchResults')}
                  {`"${searchText}"`}
                </IonLabel>
              </IonItem>
            )}
          </IonList>
        )}
        {getPnnvSection()}
        <IonFooter className='ion-no-border'>
          <IonToolbar color='light' className='p-5'>
            <IonButtons>
              {!hideClearButton && (
                <IonButton
                  data-testid='SingleSelectModalClear'
                  className='btn'
                  color='danger'
                  fill='outline'
                  expand='block'
                  onClick={clearInput}
                  disabled={selectedLabel === '' && !selectedPNNVOption}
                  id='singleselect-popover-clear-button'>
                  {t('common.buttons.Clear')}
                </IonButton>
              )}
              <IonButton
                data-testid='SingleSelectModalCancel'
                className={`btn ${
                  customInput && selectedOption?.id === customInput.textboxEnabledId ? 'btn-primary' : 'btn-secondary'
                }`}
                expand='block'
                id='singleselect-popover-cancel-button'
                onClick={() => {
                  dismiss()
                  pnnvDoneAction && pnnvDoneAction()
                }}>
                {customInput && selectedOption?.id === customInput.textboxEnabledId
                  ? t('common.buttons.Save')
                  : t('common.buttons.Cancel')}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </>
    </ResizableBox>
  )

  const modalSelectBody = (
    <>
      <IonHeader className='ion-no-border'>
        <IonToolbar color='light'>
          <IonListHeader color='light'>
            <IonLabel>{listHeader ?? pnnvListHeader}</IonLabel>
          </IonListHeader>
        </IonToolbar>
        {!showPNNVAsNormals && (
          <IonToolbar color='light'>
            <IonSearchbar className='display-none' />
            <IonSearchbar
              data-testid='Searchbar'
              debounce={0}
              value={searchText}
              onIonChange={handleSearch}
              autocomplete='off'></IonSearchbar>
          </IonToolbar>
        )}
      </IonHeader>

      {!showPNNVAsNormals && (
        <IonList id='selectList'>
          {isLoading && <IonSpinner data-testid='Spinner' />}
          {options && filteredOptions && (
            <>
              {filteredOptions.map((option: any) => {
                const isTextboxEnabled =
                  selectedOption &&
                  selectedOption.id === customInput?.textboxEnabledId &&
                  option.id === customInput?.textboxEnabledId
                return (
                  <React.Fragment key={`option_${option.id}`}>
                    <IonItem
                      data-private={isPHI}
                      style={getStyleForPHI(isPHI)}
                      button
                      detail={false}
                      data-testid={`Option_${option.id}`}
                      onClick={(e) => selectOption(e, option)}
                      className='ion-no-padding ion-activatable'>
                      <IonLabel
                        id={`optionLabel_${option.id}`}
                        className={`${
                          selectedOption && String(selectedOption.id) === String(option.id) ? 'selectedLabel' : ''
                        }
                    ${'optionLabel'}`}>
                        {showSelectedOptionId
                          ? option[showSelectedOptionId] != null
                            ? `(${option[showSelectedOptionId]}) ${option.name}`
                            : option.name
                          : option.name}
                      </IonLabel>
                      <IonRippleEffect />
                    </IonItem>
                    {isTextboxEnabled && customInput?.textboxSetAction && (
                      <div data-private={isPHI} style={getStyleForPHI(isPHI)}>
                        <SInput
                          label='Describe'
                          color='primary'
                          dataTestID={`Other-Input_${customInput?.textboxEnabledId}`}
                          inputValue={customInput?.textboxValue ? customInput?.textboxValue : ''}
                          onChangeAction={customInput?.textboxSetAction}
                          maxlength={customInput?.maxlength ? customInput?.maxlength : undefined}
                        />
                        {customInput?.setTextBoxActionName && (
                          <>
                            <Validation id={customInput?.setTextBoxActionName} />
                            <hr />
                          </>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                )
              })}
            </>
          )}
          {searchText.length > 0 && !filteredOptions.length && (
            <IonItem key={'no_options_found'}>
              <IonLabel>
                {t('common.NoSearchResults')}
                {`"${searchText}"`}
              </IonLabel>
            </IonItem>
          )}
        </IonList>
      )}
      {getPnnvSection()}
      <IonFooter className='ion-no-border'>
        <IonToolbar color='light'>
          <IonButtons>
            {!hideClearButton && (
              <IonButton
                data-testid='SingleSelectModalClear'
                className='btn'
                color='danger'
                fill='outline'
                expand='block'
                onClick={clearInput}
                disabled={selectedLabel === '' && !selectedPNNVOption}
                id='singleselect-modal-clear-button'>
                {t('common.buttons.Clear')}
              </IonButton>
            )}
            <IonButton
              data-testid='SingleSelectModalCancel'
              className={`btn ${
                customInput && selectedOption?.id === customInput.textboxEnabledId ? 'btn-primary' : 'btn-secondary'
              }`}
              expand='block'
              onClick={() => {
                dismiss()
                pnnvDoneAction && pnnvDoneAction()
              }}
              id='singleselect-modal-cancel-button'>
              {customInput && selectedOption?.id === customInput.textboxEnabledId
                ? t('common.buttons.Save')
                : t('common.buttons.Cancel')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </>
  )

  return (
    <>
      <div className={shouldRenderPNNVBar ? '' : 'input-field-wrapper'}>
        <IonModal
          data-testid='SingleSelectModal'
          className={isIOS ? 'single-select-mobile single-select-mobile-ios' : 'single-select-mobile'}
          showBackdrop={showModal}
          isOpen={showModal}
          onDidDismiss={resetModalState}
          backdropDismiss>
          {modalSelectBody}
        </IonModal>

        <IonPopover
          data-testid='SingleSelectPopover'
          className='single-select-popover'
          showBackdrop={popoverState.showPopover}
          isOpen={popoverState.showPopover}
          event={popoverState.event}
          onDidDismiss={resetPopoverState}>
          {popoverSelectBody}
        </IonPopover>

        {!shouldRenderPNNVBar && (
          <IonItem
            ref={itemRef}
            button
            detail={false}
            disabled={disabled}
            data-testid={dataTestID}
            lines='none'
            className='single-select-ion-item'
            onClick={(e) => showSingleSelect(e)}>
            {reduxAction && <ValidationBadgeComponent action={reduxAction} />}
            {label && (
              <IonLabel
                position={selectedPNNVOption || selectedLabel ? 'floating' : undefined}
                className={
                  selectedPNNVOption || selectedLabel ? 'single-select-ion-label-floating' : 'single-select-ion-label'
                }
                style={getStyleForPHI(isPHI)}
                color='medium'>
                {label}
              </IonLabel>
            )}
            <div
              data-private={isPHI}
              className={selectedPNNVOption || selectedLabel ? 'selected-option' : 'hidden-selected-option '}>
              {selectedPNNVOption ? selectedPNNVOption.name : selectedLabel ? getLabelValue() : ''}
            </div>
            <IonIcon
              slot={label || selectedOption ? 'end' : 'start'}
              className='ion-icon-end'
              size='small'
              color='medium'
              icon={caretDownOutline}
            />
          </IonItem>
        )}
      </div>
      {shouldRenderPNNVBar && !pnnvRenderedFromFreeTextField && (
        <IonToolbar
          className={showSingleSelectPopup || showModal || popoverState.showPopover ? 'hidden-for-PNNV-option' : ''}>
          <IonButtons slot='start'>
            <IonButton
              color='danger'
              ref={pnnvBarRef}
              onClick={(e) => {
                if (actionOnCannotComplete) actionOnCannotComplete()
                dispatch(setNumberPadAndCalendarPNNVIsOpen(true))
                showSingleSelect(e)
              }}>
              {t('pnnv.cannotComplete')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton color='primary' data-testid='doneButton' onClick={pnnvDoneAction}>
              {t('pnnv.done')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      )}
      {shouldRenderPNNVBar && pnnvRenderedFromFreeTextField && (
        <IonButton
          className='display-none'
          ref={hiddenPNNVBarRef}
          onClick={() => {
            if (pnnvState.freeTextField.options.length) {
              showSingleSelect(pnnvState.freeTextField.customEvent, true)
            }
          }}></IonButton>
      )}
    </>
  )
}

export default SingleSelect
